import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit
} from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    Subject,
    filter,
    first,
    map,
    take,
    takeUntil
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { LgvLoanApp } from '../../../data-models/lgv-loan-application.model';
import { NameValueNumericPair } from '../../../data-models/name-value-pair';
import { LgvLoanApplicationService } from '../../../core/services/lgv-loan-application.service';
import {
    LgvAPIService,
    LgvClientService,
    LgvConstants,
    LgvUserService,
    NotificationService
} from '../../../core/core.module';
import { ConfirmationDialogComponent } from '../../../shared/confirmation-dialog/confirmation-dialog.component';
import { LgvGridManager } from '../../../shared/grid-manager/grid-manager';
import { LoanAppTaskModalComponent } from '../../../shared/loan-app-task-modal/loan-app-task-modal.component';
import { environment as env } from '../../../../environments/environment';
import { DocumentViewerModalComponent } from '../../document-viewer-modal/document-viewer-modal.component';
import { LoanAppPageService } from '../../../core/services/page-services/loan-app-page.service';
import { LgvAuthMgrService } from '../../../core/services/lgv-auth-mgr.service';
import { CreditReportDialogComponent } from '../credit-report-dialog/credit-report-dialog.component';
import { LgvPricingService } from '../../../features/pricing/services/pricing.service';
import { PricingCode } from '../../../features/pricing/enums/pricing.enum';
import { hasDialogAction } from '../../../core/utility/dialog-utils';

@Component({
    selector: 'lgv-loan-app-tasks',
    templateUrl: './loan-app-tasks.component.html',
    styleUrls: ['./loan-app-tasks.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoanAppTasksComponent implements OnInit, OnDestroy {
    isLoading$ = new BehaviorSubject(false);
    onDestroy$ = new Subject();
    loanAppId: string = null;
    GridMgr: LgvGridManager;
    filesToUpload: File[] = null;

    _tasks: LgvLoanApp.Task[] = null;
    tasks$ = new BehaviorSubject(this._tasks);

    unfilteredTasks: LgvLoanApp.Task[] = null;
    previousStatus = '';

    _taskStatusList: NameValueNumericPair[] = null;
    taskStatusList$ = new BehaviorSubject(this._taskStatusList);

    get firstBorrower$(): Observable<LgvLoanApp.Borrower> {
        return this.loanAppPageSvc.selectedLoanApplication$.pipe(
            filter(f => f !== null),
            map(x =>
                x.loanInfo?.borrowers?.length > 0
                    ? x.loanInfo.borrowers[0]
                    : null
            )
        );
    }
    get secondBorrower$(): Observable<LgvLoanApp.Borrower> {
        return this.loanAppPageSvc.selectedLoanApplication$.pipe(
            filter(f => f !== null),
            map(x =>
                x.loanInfo?.borrowers?.length > 1
                    ? x.loanInfo.borrowers[1]
                    : null
            )
        );
    }

    get isClient(): boolean {
        return this.lgvAuthMgrSvc.IsClient;
    }
    get isDLR(): boolean {
        return this.lgvUserSvc.IsDLR;
    }
    get isCK(): boolean {
        return this.lgvUserSvc.IsCK;
    }
    get isInternalUser(): boolean {
        return !this.isClient;
    }

    constructor(
        public loanAppPageSvc: LoanAppPageService,
        private route: ActivatedRoute,
        private http: HttpClient,
        private router: Router,
        public lgvAuthMgrSvc: LgvAuthMgrService,
        public lgvUserSvc: LgvUserService,
        public lgvClientSvc: LgvClientService,
        public lgvApiSvc: LgvAPIService,
        public matDialog: MatDialog,
        private notificationSvc: NotificationService,
        private lgvLoanAppSvc: LgvLoanApplicationService,
        private authMgr: LgvAuthMgrService
    ) {
        this.GridMgr = new LgvGridManager();
        this.GridMgr.Init('status', 'asc');
    }

    ngOnInit(): void {
        this.loanAppPageSvc.selectedLoanApplication$
            .pipe(
                filter(f => !!f),
                takeUntil(this.onDestroy$)
            )
            .subscribe(la => {
                this.loanAppId = this.loanAppPageSvc.selectedLoanApplication$.value.id;

                this.sortTasks();
                this.taskStatusList$.next(
                    this.lgvLoanAppSvc.buildTaskStatusList(this.tasks$.value)
                );

                // this.isLoading$.next(false);
            });

        this.GridMgr.OnRefreshData.pipe(takeUntil(this.onDestroy$)).subscribe(
            refreshData => {
                let sortedArray = [];
                console.log(refreshData?.data);
                if (refreshData?.data?.direction === 'asc') {
                    sortedArray = this.tasks$.value.sort((a, b) =>
                        a[refreshData?.data?.column] >
                        b[refreshData?.data?.column]
                            ? 1
                            : -1
                    );
                } else {
                    sortedArray = this.tasks$.value.sort((a, b) =>
                        a[refreshData?.data?.column] <
                        b[refreshData?.data?.column]
                            ? 1
                            : -1
                    );
                }
                this.tasks$.next(sortedArray);
            }
        );
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    sortTasks() {
        //let property = this.GridMgr.CurrentSortColumn;

        let sortedTasks = [];
        let aVal = '';
        let bVal = '';
        let isDesc = this.GridMgr.CurrentSortDirection === 'desc';

        sortedTasks = this.loanAppPageSvc.selectedLoanApplication$.value.tasks.sort(
            (a, b) => {
                switch (this.GridMgr.CurrentSortColumn) {
                    case 'status':
                        aVal = a.taskStatusTimestamp;
                        bVal = b.taskStatusTimestamp;
                        break;
                    case 'type':
                        aVal = a.type.name;
                        bVal = b.type.name;
                        break;
                    default:
                        aVal = a[this.GridMgr.CurrentSortColumn];
                        bVal = b[this.GridMgr.CurrentSortColumn];
                        break;
                }

                let rtnVal = aVal > bVal ? -1 : 1;

                //console.log(`sort: ${aVal} vs ${bVal} | isDesc: ${isDesc} | origVal: ${rtnVal}  | final: ${isDesc ? rtnVal * -1 : rtnVal} `);

                return isDesc ? rtnVal * -1 : rtnVal;
            }
        );

        this.tasks$.next(sortedTasks);
        this.unfilteredTasks = sortedTasks;
    }

    getIcon(task: any): string {
        if (task.type.name === 'Form') {
            return 'file-lines';
        } else if (task?.item?.id == 'e51f782a-7a19-f011-aaa7-000d3a5bfc2b') {
            return 'file-invoice-dollar';
        } else {
            return 'file-arrow-up';
        }
    }

    taskIs1003(t: LgvLoanApp.Task) {
        //return t && t?.id.toUpperCase() === LgvConstants.TaskType1003.toUpperCase();
        return (
            t && (t?.item?.name?.includes('1003') || t?.name?.includes('1003'))
        );
    }

    taskHasDocumentReady(t: LgvLoanApp.Task) {
        return (
            t &&
            t.document &&
            t?.document?.id &&
            t.document.isPdfConversionComplete
        );
    }

    taskIsPendingCreditReport(t: LgvLoanApp.Task) {
        // allow open only for credit reports not yet accepted
        return (
            t &&
            t.name.includes('Credit Report') &&
            t.status?.name !== 'Accepted'
        );
    }

    taskIsDisclosure(t) {
        return t && t.type.name === 'Disclosure';
    }

    isOpenAllowed(t: any): boolean {
        // return true;  // loan app will handle "read only" mode if necessary
        return (
            this.taskIs1003(t) ||
            this.taskHasDocumentReady(t) ||
            this.taskIsPendingCreditReport(t) ||
            this.taskIsDisclosure(t) ||
            t?.item?.id == 'e51f782a-7a19-f011-aaa7-000d3a5bfc2b'
        );
        // return task.status.name === 'Requested' && task.type.name === 'Form';
    }

    isUploadAllowed(task: any): boolean {
        return (
            task.type.name !== 'Form' &&
            task.type.name !== 'Disclosure' &&
            task.status.name !== 'Uploaded' &&
            task.status.name !== 'Accepted' &&
            task?.item?.id != 'e51f782a-7a19-f011-aaa7-000d3a5bfc2b'
        );
    }

    isDeleteAllowed(task: any): boolean {
        return (
            task.status.name !== 'Accepted' &&
            (task.type.name !== 'Form' ||
                task.name.includes('Credit Report') ||
                task.type.name !== 'Disclosure') &&
            task?.item?.id != 'e51f782a-7a19-f011-aaa7-000d3a5bfc2b'
        );
    }

    isEditAllowed(task: any): boolean {
        return (
            task.status.name !== 'Accepted' &&
            task.type.name !== 'Form' &&
            task.type.name !== 'Disclosure' &&
            task?.item?.id != 'e51f782a-7a19-f011-aaa7-000d3a5bfc2b'
        );
    }

    isDuplicateAllowed(task: any): boolean {
        return (
            task.type.name !== 'Form' &&
            task.type.name !== 'Disclosure' &&
            task?.item?.id != 'e51f782a-7a19-f011-aaa7-000d3a5bfc2b'
        );
    }

    getTooltip(task: any): string {
        return this.isEditAllowed(task) || this.isOpenAllowed(task)
            ? task.name.includes('Credit Report')
                ? 'Start Task'
                : 'Edit Task'
            : 'Task Editing Not Allowed';
    }

    onAddTask() {
        this.openTaskModal('addTask', null);
    }

    onEditTask(t: LgvLoanApp.GenericType) {
        if (this.lgvUserSvc.isPreLicensedAgent && !this.authMgr.IsClient) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }

        this.openTaskModal('editTask', t);
    }

    handleClick(task: any): void {
        if (this.isUploadAllowed(task)) {
            this.onUploadDocument(task);
        } else if (this.isOpenAllowed(task)) {
            if (
                task.type.name === 'Form' &&
                !task.name.includes('Credit Report')
            ) {
                this.onGoToLoanApplication();
            } else if (
                task.type.name === 'Form' &&
                task.name.includes('Credit Report')
            ) {
                this.openTaskModal(null, task);
            } else if (
                task?.item?.id == 'e51f782a-7a19-f011-aaa7-000d3a5bfc2b'
            ) {
                this.goToPricing();
            } else {
                this.openTaskDocument(task);
            }
        }
    }

    getColor(task: any): string {
        return this.isUploadAllowed(task) || this.isOpenAllowed(task)
            ? 'initial'
            : 'grey';
    }

    onGoToLoanApplication() {
        if (!this.isClient) {
            this.loanAppPageSvc.openEconsentDialog(
                `/admin/admin-view-loan-app/appId/${this.loanAppId}/-1/-1`,
                this.loanAppId,
                this.isClient
            );
        } else {
            this.loanAppPageSvc.openEconsentDialog(
                `/client/loan-applications/${this.loanAppId}/-1/-1`,
                this.loanAppId,
                this.isClient
            );
        }
    }

    goToDocMagic(task: LgvLoanApp.Task) {
        this.isLoading$.next(true);
        this.lgvLoanAppSvc
            .getDisclosureTask(this.loanAppId, task.id)
            .pipe(take(1))
            .subscribe(disclosure => {
                this.isLoading$.next(false);
                window.open(disclosure?.participant?.accessUri, '_blank');
            });
    }

    onUploadDocument(t: LgvLoanApp.GenericType) {
        if (this.lgvUserSvc.isPreLicensedAgent && !this.authMgr.IsClient) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }

        this.openTaskModal('uploadDocument', t);
    }

    onConfirmDeleteTask(t: LgvLoanApp.GenericType) {
        if (this.lgvUserSvc.isPreLicensedAgent && !this.authMgr.IsClient) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }

        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            width: '250px',
            data: { Confirmation: `Are you sure you want to delete this task?` }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (hasDialogAction(x)) {
                    this.lgvLoanAppSvc
                        .deleteTask(t.id, this.loanAppId)
                        .subscribe(
                            x => {
                                this.notificationSvc.success(
                                    'Task has been removed.'
                                );

                                this.removeTask(t);
                            },
                            err => {
                                this.notificationSvc.error(
                                    `There was an error trying to remove this task`
                                );
                            }
                        );
                }
            });
    }

    removeTask(t: LgvLoanApp.GenericType) {
        const tmpArray = this.loanAppPageSvc.selectedLoanApplication$.value
            .tasks;

        const remIndex = tmpArray.findIndex(x => x.id == t.id);
        //console.log('Remove index: ' + remIndex);

        if (remIndex > -1) {
            tmpArray.splice(remIndex, 1);
            this.loanAppPageSvc.selectedLoanApplication$.value.tasks = tmpArray;
            this.GridMgr.SetTotalResultSize(this.GridMgr.TotalResults - 1);
            this.sortTasks();
            this.taskStatusList$.next(
                this.lgvLoanAppSvc.buildTaskStatusList(this.tasks$.value)
            );
        }
    }

    onDeleteTask(t: LgvLoanApp.GenericType) {
        this.openTaskModal('editTask', t);
    }

    openTaskModal(mode: string, taskItem: LgvLoanApp.GenericType) {
        if (!taskItem?.name.includes('Credit Report')) {
            this.lgvUserSvc.AgentDTO$.pipe(
                filter(f => f !== null),
                take(1)
            ).subscribe(agent => {
                const dialogRef = this.matDialog.open(
                    LoanAppTaskModalComponent,
                    {
                        width: '800px',
                        panelClass: 'dialog-container',
                        //disableClose: true,
                        data: {
                            clientId: this.isClient
                                ? this.lgvClientSvc.ClientID$.value
                                : null,
                            agentDTO: agent,
                            loanAppId: this.loanAppId,
                            mode: mode,
                            taskItem: taskItem
                                ? taskItem
                                : this.tasks$.value[0],
                            selectedFile: this.filesToUpload
                                ? this.filesToUpload
                                : null,
                            taskList: this.tasks$.value
                        }
                    }
                );

                dialogRef
                    .afterClosed()
                    .pipe(first())
                    .subscribe(x => {
                        this.filesToUpload = null;
                        if (hasDialogAction(x)) {
                            this.loanAppPageSvc.loadLoanApp(this.loanAppId);
                        }
                    });
            });
        } else {
            const dialogRef = this.matDialog.open(CreditReportDialogComponent, {
                width: '500px',
                panelClass: 'dialog-container',
                //disableClose: true,
                data: {
                    loanApp: this.loanAppPageSvc.selectedLoanApplication$.value,
                    clientId: this.isClient
                        ? this.lgvClientSvc.ClientID$.value
                        : null,
                    taskItem: taskItem
                }
            });

            dialogRef
                .afterClosed()
                .pipe(first())
                .subscribe(x => {
                    this.filesToUpload = null;
                    if (hasDialogAction(x)) {
                        this.loanAppPageSvc.loadLoanApp(this.loanAppId);
                    }
                });
        }
    }

    onDuplicateTask(task) {
        if (this.lgvUserSvc.isPreLicensedAgent && !this.authMgr.IsClient) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }

        delete task.taskStatusTimestamp;

        this.lgvLoanAppSvc
            .addTask(task, this.loanAppId)
            .pipe(take(1))
            .subscribe(
                x => {
                    this.notificationSvc.success(`Task has been duplicated.`);
                    this.loanAppPageSvc.loadLoanApp(this.loanAppId);
                },
                err => {
                    this.notificationSvc.error(
                        `There was an error adding ${task.name} to the application.`
                    );
                }
            );
    }

    goToPricing() {
        const readyToLockPricing = this.lgvClientSvc.pricingScenarios$.value.find(
            ps => ps.status?.code == PricingCode.ReadyToLock
        );
        let baseUrl;
        if (this.isClient) {
            baseUrl = `client/loan-app/${this.loanAppPageSvc.selectedLoanApplication$?.value?.id}/quotes`;
        } else {
            baseUrl = `admin/loan-app/${this.loanAppPageSvc.selectedLoanApplication$?.value?.id}/quotes`;
        }
        this.router.navigate([baseUrl]);
    }

    //#region Drag n Drop

    uploadBoxCSS = '';

    containsFiles(event) {
        if (event.dataTransfer.types) {
            for (var i = 0; i < event.dataTransfer.types.length; i++) {
                if (event.dataTransfer.types[i] == 'Files') {
                    return true;
                }
            }
        }
        return false;
    }

    onDragOver(event) {
        event.preventDefault();
        //console.log('onDragOver...');
        if (this.containsFiles(event)) {
            this.uploadBoxCSS = 'file-hover';
        }
    }

    onDragLeave(event) {
        event.preventDefault();
        //console.log('onDragLeave...');
        this.uploadBoxCSS = '';
    }

    onDropSuccess(event) {
        //console.log('onDropSuccess()');
        //console.log(event);
        event.preventDefault();
        this.uploadBoxCSS = '';

        this.onFileChange(event.dataTransfer.files); // notice the "dataTransfer" used instead of "target"
    }

    onClickEvent(event) {
        this.openTaskModal('uploadDocument', null);
    }

    // From attachment link
    onChange(event) {
        console.log('onChange()');
        this.onFileChange(event.target.files); // "target" is correct here
    }

    private onFileChange(files: File[]) {
        console.log('onFilesChanged');
        console.log(files);
        this.filesToUpload = files;
        this.openTaskModal('uploadDocument', null);
    }

    //#endregion Drag n Drop

    onOpenTaskItem(t: LgvLoanApp.Task) {
        if (this.lgvUserSvc.isPreLicensedAgent && !this.authMgr.IsClient) {
            this.lgvUserSvc.showPreLicensingModal();
            return;
        }

        if (this.taskIs1003(t)) {
            this.onGoToLoanApplication();
        } else if (this.taskHasDocumentReady(t)) {
            this.openTaskDocument(t);
        } else if (this.taskIsPendingCreditReport(t)) {
            this.openTaskModal(null, t);
        } else if (this.taskIsDisclosure(t)) {
            this.goToDocMagic(t);
        } else if (t?.item?.id == 'e51f782a-7a19-f011-aaa7-000d3a5bfc2b') {
            this.goToPricing();
        }
    }

    openTaskDocument(t: LgvLoanApp.Task) {
        if (!this.taskHasDocumentReady(t)) {
            this.notificationSvc.info(
                'This task does not have documents ready to view'
            );
        }

        const dialogRef = this.matDialog.open(DocumentViewerModalComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            height: '95%',
            width: '95%',
            data: {
                applicationId: this.loanAppId,
                task: t,
                newWindow: false
            }
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(response => {
                if (hasDialogAction(response)) {
                    this.loanAppPageSvc.loadLoanApp(this.loanAppId);
                }
            });
    }

    toggleDetail(task: any) {
        if (task.status.name !== 'Requested') {
            task.expanded = !task.expanded;
        } else if (this.authMgr.IsClient) {
            this.handleClick(task);
        }
    }

    getStatusClass(status: string) {
        return {
            'status-requested': status === 'Requested',
            'status-accepted': status === 'Accepted',
            'status-uploaded': status === 'Uploaded',
            'status-rejected': status === 'Rejected'
        };
    }

    getTootltipText(taskStatusTimestamp, status) {
        return `${status} on ${taskStatusTimestamp}`;
    }

    getStatusTootltipText(count, status) {
        return `${count} tasks ${
            status === 'Rejected' ? 'Re-requested' : status
        }`;
    }

    filterStatuses(status) {
        let filteredTasks;
        if (this.previousStatus != status) {
            filteredTasks = this.unfilteredTasks.filter(
                t => t.status?.name == status
            );
        } else {
            filteredTasks = this.unfilteredTasks;
        }
        this.tasks$.next(filteredTasks);
        this.previousStatus = status;
    }
}
